import { Card, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconBrandWhatsapp, IconDotsVertical, IconPencil, IconStarFilled, IconX } from '@tabler/icons-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMemo, useRef, useState } from 'react'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import { useDrives } from '@/stores/useDrives.ts'
import { getDriveDefaultsFor } from '@/controller/driveDefaults.ts'
import { HeyDriveType, DriveThemeStyleDefinition, DriveContact, HeyDrive } from '@/models/drive.ts'
import { Trans, useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { getCurrentLanguageInfos } from '@/controller/languageHelper.ts'
import { shallHideElementsForScreenshot } from '@/controller/demoHelper.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { useClient } from '@/stores/useClient.ts'
import { Album } from '@/models/album.ts'
import PhotosByContact from '@/components/PhotosByContact.tsx'
import { getThumbnailFromAlbums } from '@/models/album.ts'

function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString(i18next.language || 'de', { day: 'numeric', month: 'long', year: 'numeric' })
}

interface ShareModeData {
  driveTitle: string
  driveType: string
  title?: string
  albumThumbnail?: string | null
  albumDescription?: string
  headerStyle?: string | null
  eventDate?: string | null
  previewDesktopUrl?: string | null
  previewMobileUrl?: string | null
  previewDesktopCenterX?: number
  previewDesktopCenterY?: number
  previewMobileCenterX?: number
  previewMobileCenterY?: number
  contact?: DriveContact | null
}

interface DriveHeaderProps {
  title?: string | null
  description?: string | null
  onChangeImageClick?: () => void
  shareMode?: ShareModeData
  theme?: DriveThemeStyleDefinition | null
  primaryAlbum?: Album | null
  contact?: DriveContact | null
}

export default function DriveHeader({ title, description, onChangeImageClick, shareMode, theme, primaryAlbum, contact }: DriveHeaderProps) {
  const { driveId } = useParams<{ driveId: string }>()
  const navigate = useNavigate()
  const demoModalRef = useRef<HTMLDialogElement>(null)
  const filesContext = useFilesContext()
  const album = shareMode ? null : filesContext?.currentAlbum
  const { drives } = useDrives()
  const drive = shareMode ? null : drives[driveId!]
  const driveThumbnail = shareMode?.albumThumbnail || (drive ? getThumbnailFromAlbums(drive.albums) : null) || album?.thumbnail || null
  const { profile, setGuestMode, isGuestMode, logout } = useProfile()
  const isAuthor = !shareMode && profile && drive && profile.username == drive.author
  const showSettings = isAuthor && !shallHideElementsForScreenshot()
  const showDisableGuestMode = !shareMode && isGuestMode
  const settingsRef = useRef<HTMLDialogElement>(null)
  const shallShowOldUpgrade = !shareMode && profile && profile.country_code && profile.country_code.toLowerCase() == 'ch'
  const { initGalleryUpgrade } = useClient()
  const [isLoadingStripe, setIsLoadingStripe] = useState(false)
  const { t } = useTranslation()

  function onTitleClick() {
    if (shareMode) return
    navigate(`/d/${driveId}/${album!.id}`)
  }
  function onHeaderClick() {
    if (shareMode) return
    navigate(`/d/${drive!.id}/${album!.id}`)
  }

  function onDemoUpgradeClick() {
    demoModalRef.current?.showModal()
  }

  if (!shareMode && (!drive || !album)) {
    return null
  }

  function headerContent() {
    if (shareMode) {
      return shareMode.driveTitle || 'FridaySnap'
    }

    if (drive!.is_ha) {
      return 'HeyAlbum'
    }

    if (drive!.custom_logo) {
      return <div className='flex flex-row justify-center items-center h-[4rem]'>
        <img src={drive!.custom_logo} className='max-w-[11rem] max-h-[4rem]'/>
      </div>
    }

    return 'FridaySnap'
  }

  function onSettingsClick() {
    settingsRef.current!.showModal()
  }

  function onGuestModeEnable() {
    setGuestMode(true)
    navigate(`/d/${driveId}/${album!.id}`)
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }
  function onGuestModeDisable() {
    setGuestMode(false)
    navigate(`/d/${driveId}/${album!.id}`)
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  function onLogoutClick() {
    logout()
    navigate(`/d/${driveId}/${album!.id}`)
    setTimeout(() => window.location.reload(), 200)
  }

  const driveType = shareMode ? (shareMode.driveType as HeyDriveType) : drive!.drive_type
  const driveDefaults = getDriveDefaultsFor(driveType)
  const defaultPicture = driveDefaults.previewPicture
  const info = getCurrentLanguageInfos()
  const demoUpgradeLink = shareMode ? null : {
    [HeyDriveType.WEDDING]: info.weddingProductPage,
    [HeyDriveType.BIRTHDAY]: 'https://fridaysnap.de/produkt/qr-code-geburtstags-fotos/',
    [HeyDriveType.PARTY]: 'https://fridaysnap.de/produkt/qr-code-party-fotos/',
    [HeyDriveType.BABY_SHOWER]: 'https://fridaysnap.de/produkt/qr-code-baby-shower-party-fotos/',
    [HeyDriveType.COMPANY]: 'https://fridaysnap.de/produkt/qr-code-firmenfeier-fotos/',
    [HeyDriveType.PROF]: null,
  }[drive!.drive_type]

  function renderDemoUpgradeContent() {
    if (shallShowOldUpgrade) {
      return <>
        <p>{t('common.thisIsYourDemo')}</p>
        <p>
          <Trans i18nKey='common.howToUpgrade'>
            <a
              className='text-primary underline font-bold'
              href={demoUpgradeLink ?? undefined} target='_blank' />
          </Trans>
          😊
        </p>
        <Button color='primary' className='p-0'>
          <a className='w-full h-full flex items-center justify-center'
             href={demoUpgradeLink ?? undefined} target='_blank'
          >{t('common.upgradeDemo')}</a>
        </Button>
        <form method="dialog" className='flex-1'>
          <Button variant='outline' color='neutral' className='w-full border-none'>{t('common.cancel')}</Button>
        </form>
      </>
    }

    return <>
      <Card className='bg-neutral'>
        <Card.Body className='px-2 py-4 flex flex-col gap-3'>
          <ul className='flex flex-col gap-2'>
            <li>✅ {t('common.monthsStorageDuration', {months: 12})}</li>
            <li>✅ {t('common.storageDurationStartsOnEventDay')}</li>
            <li>✅ {t('common.unlimitedPhotosVideos')}</li>
            <li>✅ {t('common.photoGamesPrivacyLiveshow')}</li>
            <li>✅ {t('common.couplesUsedFridaysnap', {count: 5000})}</li>
            <li>✅ {t('common.albumsUseMultipleDays')}</li>
            <li>✅ {t('common.unlimitedMassDownloads')}</li>
            <li>✅ {t('common.whatsappSupportQuestion')}</li>
          </ul>
          <p>
            <a href="https://de.trustpilot.com/review/fridaysnap.de" className='flex flex-row justify-center items-center whitespace-pre-wrap'>
              <Trans i18nKey='common.reviewsTrustpilot' t={t} values={{count: '187', rating: '4.9'}}>
                <IconStarFilled size={12} className='me-1 text-[orange]' />
              </Trans>
            </a>
          </p>
          {/*<p>Alle Informationen gibt es auf unserer <a*/}
          {/*  className='text-primary underline font-bold'*/}
          {/*  href={demoUpgradeLink} target='_blank'>Produktseite</a></p>*/}
          <div>
            <Button color='primary'>
              <a className='w-full h-full flex flex-row items-center px-2 justify-center gap-1'
                 href='https://wa.me/496987000170' target='_blank'
              >
                <IconBrandWhatsapp width={20} height={20}/>
                <div className='flex flex-col gap-1'>
                  {t('common.moreQuestions')} <br />
                  <b>{t('common.personalWhatsappGuidance')}</b>
                </div>
              </a>
            </Button>
          </div>
        </Card.Body>
      </Card>
      <p className='text-center font-bold text-xl'> 39,99 €</p>
      <Button color='primary' className='p-0' onClick={onUpgradeClick} loading={isLoadingStripe} disabled={isLoadingStripe}>
        <span className='w-full h-full flex items-center justify-center text-xl'>
          {t('common.buyNowAction')}
        </span>
      </Button>
      <div className='flex flex-col justify-center'>
        <p className='text-sm opacity-50'>{t('common.upgradeImmediatelyAvailable')}</p>
        <p className='text-sm opacity-50'>{t('common.storageDurationStartsAfterUsage')}</p>
      </div>
      <form method="dialog" className='flex-1'>
        <Button variant='outline' color='neutral' className='w-full border-none'>{t('common.cancel')}</Button>
      </form>
    </>
  }

  async function onUpgradeClick() {
    if (isLoadingStripe) {
      return
    }

    setIsLoadingStripe(true)
    try {
      const res = await initGalleryUpgrade(drive!)
      const data: {
        session_id: string
        stripe_session_id: string
        checkout_url: string
        status: string
        expires_at: string
      } = res.data
      window.location.href = data.checkout_url
    } catch (e) {
      setIsLoadingStripe(false)
      alert(t('common.paymentErrorContactSupport'))
    }
  }

  // Render themed header variant if theme is set
  if (theme && drive) {
    return (
      <ThemedHeaderVariant
        drive={drive}
        theme={theme}
        primaryAlbum={primaryAlbum || null}
        contact={contact ?? drive.contact}
      />
    )
  }

  // Render themed header in share/portfolio mode
  if (shareMode?.headerStyle) {
    const desktopPos = `${(shareMode.previewDesktopCenterX ?? 0.5) * 100}% ${(shareMode.previewDesktopCenterY ?? 0.5) * 100}%`
    const mobilePos = `${(shareMode.previewMobileCenterX ?? 0.5) * 100}% ${(shareMode.previewMobileCenterY ?? 0.5) * 100}%`
    const headerProps: ThemedHeaderVariantInnerProps = {
      customLogo: null,
      logoAltText: shareMode.driveTitle,
      displayTitle: shareMode.driveTitle || 'FridaySnap',
      eventDate: shareMode.eventDate ?? null,
      previewDesktopUrl: shareMode.previewDesktopUrl,
      previewMobileUrl: shareMode.previewMobileUrl,
      desktopPosition: desktopPos,
      mobilePosition: mobilePos,
      contact: shareMode.contact ?? null,
    }

    const styleMap: Record<string, (props: ThemedHeaderVariantInnerProps) => JSX.Element> = {
      centered: CenteredHeader,
      narrow: NarrowHeader,
      partial: PartialHeader,
      botanical: BotanicalHeader,
      editorial: EditorialHeader,
      magazine: MagazineHeader,
      minimal: MinimalHeader,
      floating: FloatingHeader,
      overlay: OverlayHeader,
      grand: GrandHeader,
    }
    const HeaderComponent = styleMap[shareMode.headerStyle] ?? CenteredHeader
    return <HeaderComponent {...headerProps} />
  }

  return <div className='flex flex-col'>
    <div className="flex flex-col gap-3 py-3 relative bg-primary pb-[3.5rem] mb-[5rem]">
      <div className='text-center'>
        <a
          className='bg-primary text-neutral font-serif text-center text-4xl my-3 block cursor-pointer'
          onClick={onHeaderClick}
          // style={{borderBottom: '1px solid rgba(255, 255, 255, 0.15)'}}
        >
          { headerContent() }
        </a>
      </div>
      <div className="container mx-auto px-3 pt-3 flex flex-col items-center gap-4">
        {/*<div className='min-w-[7rem] h-[7rem] md:w-[10rem] md:h-[10rem] rounded-full bg-cover'*/}
        {/*     style={{backgroundImage: album.thumbnail ? `url(${album.thumbnail})` : 'url(/public/logo.jpg)'}}/>*/}


        {/*<div className='flex flex-col items-center justify-center'>*/}
      </div>
      {/*<p className='px-3'>*/}
      {/*  {album.description}*/}
      {/*</p>*/}
      <div className='absolute left-[50%] top-[100%]' style={{transform: 'translateX(-50%) translateY(-50%)'}}>
        <div className='w-[8rem] h-[8rem] rounded-full bg-cover bg-center overflow-hidden'
             style={{backgroundImage: driveThumbnail ? `url(${driveThumbnail})` : `url(${defaultPicture})`}}>
          {
            onChangeImageClick &&
            <div className='h-full w-full bg-secondary bg-opacity-75 flex items-center justify-center' onClick={onChangeImageClick}>
              <Button color='primary'><IconPencil /> {t('common.changeImage')}</Button>
            </div>
          }
        </div>
      </div>
      {!shareMode && (drive!.is_demo || drive!.drive_type === HeyDriveType.COMPANY || drive!.ad_mode === 'g1') && !isGuestMode &&
        <div className='absolute bottom-0 w-full'>
          { drive!.is_demo && !shallHideElementsForScreenshot() && <>
            <div className='flex flex-row justify-between p-2'>
              <Button color='neutral' variant='outline' className='border-neutral text-neutral rounded-2xl border-2'
                      onClick={onDemoUpgradeClick}>
                <Trans i18nKey='common.upgradeDemoTwoLines' />
              </Button>
              <Button color='ghost' className='text-neutral p-0'>
                <a className='w-full h-full flex flex-row items-center px-2 justify-center gap-1'
                   href='https://wa.me/496987000170' target='_blank'
                >
                  <IconBrandWhatsapp width={20} height={20}/>
                  <div>
                    <Trans i18nKey='common.whatsappGuidanceTwoLines' />
                  </div>
                </a>

              </Button>
            </div>
            <Modal ref={demoModalRef} responsive>
              <Modal.Header className='text-center'>
                {t('common.upgradeDemo')}
                <form method="dialog">
                  <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕
                  </button>
                </form>
              </Modal.Header>
              <Modal.Body className='text-center flex flex-col gap-3'>
                { renderDemoUpgradeContent() }
              </Modal.Body>
            </Modal>
          </>}
          {
            (!drive!.is_demo && drive!.ad_mode === 'g1') && !shallHideElementsForScreenshot() && <>
              <div className='flex flex-row justify-between p-2'>
                <Button color='neutral' variant='outline' className='border-neutral text-neutral rounded-2xl border-2 max-w-[7.5rem]'>
                  <a className='w-full h-full flex items-center justify-center'
                     href={demoUpgradeLink ?? undefined} target='_blank'
                  >{t('common.backShop')}</a>
                </Button>
                <Button color='neutral' variant='outline' className='border-neutral text-neutral rounded-2xl border-2 max-w-[7.5rem]'>
                  <a className='w-full h-full flex flex-row items-center px-2 justify-center gap-1'
                     href='/demo/' target='_blank'
                  >
                    <div>
                      <Trans i18nKey='common.ownDemo' />
                    </div>
                  </a>
                </Button>
              </div>
            </>
          }
          {
            (!drive!.is_demo && drive!.ad_mode !== 'g1' && drive!.drive_type === HeyDriveType.COMPANY) && <>
              <div className='flex flex-row justify-between p-2'>
                <div></div>
                <a href='https://fridaysnap.de' target='_blank' className='text-[0.8rem] text-center text-neutral font-semibold'>{t('common.poweredBy')}<br />FridaySnap.de</a>
              </div>
            </>
          }
        </div>
      }
      {
        showDisableGuestMode && <>
          <div className='absolute right-0 top-0'>
            <Button className='px-2 m-1 max-w-[7rem] h-auto max-h-[auto]' size='sm' color='neutral' onClick={onGuestModeDisable}>
              {t('common.disableGuestMode')}
            </Button>
          </div>
        </>
      }
      {
        showSettings && <>
          <div className='absolute right-0 top-0'>
            <div className='px-3 py-3 cursor-pointer' onClick={onSettingsClick}>
              <IconDotsVertical className='text-neutral' />
            </div>
          </div>
          <Modal className='bg-neutral' ref={settingsRef} responsive>
            <Modal.Header>
              <form method="dialog">
                <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-3">
                  <IconX />
                </button>
              </form>
            </Modal.Header>
            <Modal.Body>
              <div className='flex flex-col gap-3'>
                <Button color='primary' onClick={onGuestModeEnable}>{t('common.enableGuestMode')}</Button>
                <p className='text-center mb-1'>
                  {t('common.guestModeDesc')}
                </p>
                { !drive?.is_demo && <Button variant='outline' color='primary' onClick={onLogoutClick}>{t('common.logout')}</Button> }
              </div>
            </Modal.Body>
          </Modal>
        </>
      }
    </div>

    {
      (title !== null) && <div className='text-sm mx-auto text-center max-w-[20rem] px-2 mb-5'>
        <div className='mb-2'>
          {(drive || shareMode) && <h1 className={'font-serif text-3xl mb-2 whitespace-pre-line' + (shareMode ? '' : ' cursor-pointer')} onClick={onTitleClick}>
            { title || (shareMode ? shareMode.title : drive!.title) || 'FridaySnap'}
          </h1>}
        </div>
        { description !== null &&
          <span className='whitespace-pre-wrap'>
            { description || (shareMode ? shareMode.albumDescription : album?.description) }
          </span>
        }
      </div>
    }
  </div>
}

// Themed header variant - renders fullscreen/centered/partial header based on theme.header_style
interface ThemedHeaderVariantProps {
  drive: HeyDrive
  theme: DriveThemeStyleDefinition
  primaryAlbum: Album | null
  contact: DriveContact | null
}

function ThemedHeaderVariant({ drive, theme, primaryAlbum, contact }: ThemedHeaderVariantProps) {
  const headerStyle = theme.header_style || 'centered'

  // Get preview image based on device type
  const previewDesktop = primaryAlbum?.preview_desktop
  const previewMobile = primaryAlbum?.preview_mobile
  const previewDesktopUrl = previewDesktop?.detail ?? previewDesktop?.file
  const previewMobileUrl = previewMobile?.detail ?? previewMobile?.file

  // Focal point for background positioning (default to center)
  const focalPointDesktop = useMemo(() => ({
    x: primaryAlbum?.preview_desktop_center_x ?? 0.5,
    y: primaryAlbum?.preview_desktop_center_y ?? 0.5,
  }), [primaryAlbum])

  const focalPointMobile = useMemo(() => ({
    x: primaryAlbum?.preview_mobile_center_x ?? 0.5,
    y: primaryAlbum?.preview_mobile_center_y ?? 0.5,
  }), [primaryAlbum])

  // Convert 0-1 values to percentage for object-position
  const desktopPosition = `${focalPointDesktop.x * 100}% ${focalPointDesktop.y * 100}%`
  const mobilePosition = `${focalPointMobile.x * 100}% ${focalPointMobile.y * 100}%`

  // Custom logo or title
  const displayTitle = drive.custom_logo ? null : (drive.title || 'FridaySnap')

  const headerProps: ThemedHeaderVariantInnerProps = {
    customLogo: drive.custom_logo || null,
    logoAltText: drive.title || 'FridaySnap',
    displayTitle,
    eventDate: drive.event_date || null,
    previewDesktopUrl,
    previewMobileUrl,
    desktopPosition,
    mobilePosition,
    contact,
  }

  if (headerStyle === 'centered') {
    return <CenteredHeader {...headerProps} />
  }

  if (headerStyle === 'partial') {
    return <PartialHeader {...headerProps} />
  }

  if (headerStyle === 'botanical') {
    return <BotanicalHeader {...headerProps} />
  }

  if (headerStyle === 'narrow') {
    return <NarrowHeader {...headerProps} />
  }

  if (headerStyle === 'editorial') {
    return <EditorialHeader {...headerProps} />
  }

  if (headerStyle === 'magazine') {
    return <MagazineHeader {...headerProps} />
  }

  if (headerStyle === 'minimal') {
    return <MinimalHeader {...headerProps} />
  }

  if (headerStyle === 'floating') {
    return <FloatingHeader {...headerProps} />
  }

  if (headerStyle === 'overlay') {
    return <OverlayHeader {...headerProps} />
  }

  if (headerStyle === 'grand') {
    return <GrandHeader {...headerProps} />
  }

  // Fallback to centered
  return <CenteredHeader {...headerProps} />
}

interface ThemedHeaderVariantInnerProps {
  customLogo: string | null
  logoAltText: string
  displayTitle: string | null
  eventDate: string | null
  previewDesktopUrl: string | null | undefined
  previewMobileUrl: string | null | undefined
  desktopPosition: string
  mobilePosition: string
  contact: DriveContact | null
}

// Centered: Preview image centered, "Fotos von [Name]" left (rotated), gallery name right
function CenteredHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <>
      {/* Desktop layout */}
      <div className='hidden md:flex min-h-screen w-full bg-neutral py-12'>
        <div className='w-full max-w-[1600px] mx-auto px-8 flex flex-row items-center'>
          {/* Left side: "Fotos von [Vorname]" rotated, right-aligned toward image */}
          <div className='w-[14rem] flex-shrink-0 flex items-center justify-end pr-8'>
            <PhotosByContact
              contact={contact}
              className='[writing-mode:vertical-rl] rotate-180'
            />
          </div>

          {/* Center: Preview image */}
          <div className='flex-1 flex items-center justify-center'>
            {hasPreview && (
              <div className='w-full aspect-[4/3] overflow-hidden shadow-sm'>
                {previewDesktopUrl && (
                  <img
                    src={previewDesktopUrl}
                    alt=''
                    className='w-full h-full object-cover'
                    style={{ objectPosition: desktopPosition }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right side: Gallery name, left-aligned toward image */}
          <div className='w-[14rem] flex-shrink-0 flex items-center justify-start pl-8'>
            {customLogo ? (
              <img src={customLogo} alt={logoAltText} className='max-w-[7rem] max-h-[6rem]' />
            ) : (
              <div className='flex flex-col'>
                <h1 className='font-serif text-3xl text-content font-light'>
                  {displayTitle}
                </h1>
                <div className='w-10 h-[1px] bg-primary mt-3'></div>
                {eventDate && (
                  <p className='text-sm text-content opacity-70 mt-3'>{formatEventDate(eventDate)}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className='md:hidden min-h-screen w-full flex flex-col bg-neutral'>
        {/* Image area - fills available space */}
        <div className='flex-1 flex items-center justify-center p-4'>
          {hasPreview && (
            <div className='w-full h-full rounded-lg overflow-hidden shadow-xl'>
              {(previewMobileUrl || previewDesktopUrl) && (
                <img
                  src={previewMobileUrl || previewDesktopUrl!}
                  alt=''
                  className='w-full h-full object-cover'
                  style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
                />
              )}
            </div>
          )}
        </div>

        {/* Text below image */}
        <div className='flex flex-col items-center gap-2 pb-8 px-4'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[10rem] max-h-[4rem]' />
          ) : (
            <>
              <h1 className='font-serif text-xl text-content text-center font-light'>
                {displayTitle}
              </h1>
              {eventDate && (
                <p className='text-sm text-content opacity-70'>{formatEventDate(eventDate)}</p>
              )}
            </>
          )}
          <PhotosByContact contact={contact} className='opacity-75' />
        </div>
      </div>
    </>
  )
}

// Partial: Image takes ~70% width on left, title overlays from right with neutral background
function PartialHeader({
  customLogo,
  logoAltText,
  displayTitle,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <div className='min-h-screen w-full relative bg-neutral border-b-4 md:border-b-0 border-primary'>
      {/* Image section - with padding to show neutral background around it */}
      {hasPreview && (
        <div className='absolute inset-0 md:right-[35%] md:top-24 md:bottom-24 md:left-16 overflow-hidden md:rounded-lg'>
          {/* Desktop image */}
          {previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='hidden md:block w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
          {/* Mobile image - full screen with overlay */}
          {(previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='md:hidden w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
          {/* Mobile overlay */}
          <div className='md:hidden absolute inset-0 bg-[black] bg-opacity-30' />
        </div>
      )}

      {/* Title section - overlays from right on desktop with neutral background */}
      <div className='absolute inset-0 flex items-center justify-center md:justify-end'>
        {/* Mobile: text with shadow */}
        <div className='md:hidden p-8 flex flex-col items-center gap-3'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[12rem] max-h-[5rem]' />
          ) : (
            <h1 className='font-serif text-4xl text-[white] drop-shadow-lg'>
              {displayTitle}
            </h1>
          )}
          <PhotosByContact contact={contact} className='text-[white] drop-shadow-md' />
        </div>
        {/* Desktop: text with neutral background overlapping the image */}
        <div className='hidden md:flex md:w-[40%] p-12 flex-col items-start gap-4 bg-neutral border-b-4 border-primary'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[14rem] max-h-[6rem]' />
          ) : (
            <h1 className='font-serif text-5xl text-content'>
              {displayTitle}
            </h1>
          )}
          <PhotosByContact contact={contact} />
        </div>
      </div>

    </div>
  )
}

// Botanical: Fullscreen with decorative leaves, dark overlay, centered card with title + contact
function BotanicalHeader({
  customLogo,
  logoAltText,
  displayTitle,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <div className='relative min-h-screen w-full flex items-end justify-center pb-36 md:pb-40 overflow-hidden'>
      {/* Background images */}
      {hasPreview && (
        <>
          {previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='hidden md:block absolute inset-0 w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
          {(previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='md:hidden absolute inset-0 w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
          <div className='absolute inset-0 bg-[black] bg-opacity-40' />
        </>
      )}

      {/* Decorative leaves - left */}
      <img
        src='/deco/leaves-cute.svg'
        alt=''
        className='absolute bottom-0 w-[9.5rem] lg:w-64 xl:w-[18rem] opacity-50 pointer-events-none'
        style={{ left: '8%', transform: 'translateY(10%) rotate(-8deg)' }}
      />
      {/* Decorative leaves - right (mirrored) */}
      <img
        src='/deco/leaves-cute.svg'
        alt=''
        className='absolute bottom-0 w-[9.5rem] lg:w-64 xl:w-[18rem] opacity-50 pointer-events-none'
        style={{ right: '8%', transform: 'translateY(10%) rotate(8deg) scaleX(-1)' }}
      />

      {/* Center card with title + contact */}
      <div className='relative z-10 bg-neutral bg-opacity-90 px-10 py-8 md:px-16 md:py-12 flex flex-col items-center gap-4 rounded-sm'>
        {customLogo ? (
          <img src={customLogo} alt={logoAltText} className='max-w-[15rem] max-h-[6rem] mx-auto' />
        ) : (
          <h1 className='font-serif text-4xl md:text-5xl text-primary text-center'>
            {displayTitle}
          </h1>
        )}
        <PhotosByContact contact={contact} />
      </div>
    </div>
  )
}

// Reusable: dark overlay background image with optional white border inset
function DarkBackgroundImage({
  previewDesktopUrl, previewMobileUrl, desktopPosition, mobilePosition, withBorder,
}: {
  previewDesktopUrl?: string | null
  previewMobileUrl?: string | null
  desktopPosition: string
  mobilePosition: string
  withBorder?: boolean
}) {
  const hasPreview = previewDesktopUrl || previewMobileUrl
  if (!hasPreview) return null
  return (
    <>
      {previewDesktopUrl && (
        <img
          src={previewDesktopUrl}
          alt=''
          className='hidden md:block absolute inset-0 w-full h-full object-cover'
          style={{ objectPosition: desktopPosition }}
        />
      )}
      {(previewMobileUrl || previewDesktopUrl) && (
        <img
          src={previewMobileUrl || previewDesktopUrl!}
          alt=''
          className='md:hidden absolute inset-0 w-full h-full object-cover'
          style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
        />
      )}
      <div className='absolute inset-0 bg-[black] bg-opacity-40' />
      {withBorder && (
        <div className='absolute inset-4 md:inset-6 border-[3px] border-[white] border-opacity-50 pointer-events-none z-10' />
      )}
    </>
  )
}

// Editorial: Left text column, image takes ~60% on right (screenshot 2, #1)
function EditorialHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <>
      {/* Desktop */}
      <div className='hidden md:flex h-screen w-full bg-neutral'>
        {/* Left text column */}
        <div className='w-[40%] flex flex-col justify-center px-12 lg:px-16 flex-shrink-0'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[14rem] max-h-[6rem] mb-4' />
          ) : (
            <h1 className='font-serif text-4xl lg:text-5xl text-content font-light leading-tight mb-4'>
              {displayTitle}
            </h1>
          )}
          <div className='w-12 h-[1px] bg-primary mb-4' />
          {eventDate && (
            <p className='text-sm text-content opacity-60 mb-6'>{formatEventDate(eventDate)}</p>
          )}
          <PhotosByContact contact={contact} className='opacity-75' />
        </div>

        {/* Right image */}
        <div className='w-[60%] min-h-0'>
          {hasPreview && previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
        </div>
      </div>

      {/* Mobile */}
      <div className='md:hidden min-h-screen w-full flex flex-col bg-neutral'>
        <div className='flex-1 relative'>
          {hasPreview && (previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
        </div>
        <div className='p-6 flex flex-col items-center gap-2'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[10rem] max-h-[4rem]' />
          ) : (
            <h1 className='font-serif text-2xl text-content font-light text-center'>{displayTitle}</h1>
          )}
          {eventDate && (
            <p className='text-sm text-content opacity-60'>{formatEventDate(eventDate)}</p>
          )}
          <PhotosByContact contact={contact} className='opacity-75' />
        </div>
      </div>
    </>
  )
}

// Narrow: Like centered but with a narrow/slim image in the center (screenshot 1, #2)
function NarrowHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <>
      {/* Desktop */}
      <div className='hidden md:flex min-h-screen w-full bg-neutral py-12'>
        <div className='w-full max-w-[1600px] mx-auto px-8 flex flex-row items-center'>
          {/* Left: "Fotos von" rotated */}
          <div className='w-[14rem] flex-shrink-0 flex items-center justify-end pr-8'>
            <PhotosByContact
              contact={contact}
              className='[writing-mode:vertical-rl] rotate-180'
            />
          </div>

          {/* Center: Narrow preview image */}
          <div className='flex-1 flex items-center justify-center px-4'>
            {hasPreview && (
              <div className='w-[45%] aspect-[3/5] overflow-hidden shadow-sm'>
                {previewDesktopUrl && (
                  <img
                    src={previewDesktopUrl}
                    alt=''
                    className='w-full h-full object-cover'
                    style={{ objectPosition: desktopPosition }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right: Title + date */}
          <div className='w-[14rem] flex-shrink-0 flex items-center justify-start pl-8'>
            {customLogo ? (
              <img src={customLogo} alt={logoAltText} className='max-w-[7rem] max-h-[6rem]' />
            ) : (
              <div className='flex flex-col'>
                <h1 className='font-serif text-3xl text-content font-light'>
                  {displayTitle}
                </h1>
                <div className='w-10 h-[1px] bg-primary mt-3' />
                {eventDate && (
                  <p className='text-sm text-content opacity-70 mt-3'>{formatEventDate(eventDate)}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className='md:hidden min-h-screen w-full flex flex-col bg-neutral'>
        <div className='flex-1 flex items-center justify-center p-4'>
          {hasPreview && (
            <div className='w-[90%] aspect-[3/4] overflow-hidden shadow-sm'>
              {(previewMobileUrl || previewDesktopUrl) && (
                <img
                  src={previewMobileUrl || previewDesktopUrl!}
                  alt=''
                  className='w-full h-full object-cover'
                  style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
                />
              )}
            </div>
          )}
        </div>
        <div className='flex flex-col items-center gap-2 pb-8 px-4'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[10rem] max-h-[4rem]' />
          ) : (
            <h1 className='font-serif text-xl text-content text-center font-light'>{displayTitle}</h1>
          )}
          {eventDate && (
            <p className='text-sm text-content opacity-70'>{formatEventDate(eventDate)}</p>
          )}
          <PhotosByContact contact={contact} className='opacity-75' />
        </div>
      </div>
    </>
  )
}

// Magazine: Dark overlay + white border + large stacked text centered (screenshot 2, #2)
function MagazineHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  return (
    <div className='relative min-h-screen w-full flex items-center justify-center'>
      <DarkBackgroundImage
        previewDesktopUrl={previewDesktopUrl}
        previewMobileUrl={previewMobileUrl}
        desktopPosition={desktopPosition}
        mobilePosition={mobilePosition}
        withBorder
      />

      {/* Centered content */}
      <div className='relative z-20 text-center px-6 max-w-[800px]'>
        {customLogo ? (
          <img src={customLogo} alt={logoAltText} className='max-w-[15rem] max-h-[6rem] mx-auto' />
        ) : (
          <h1 className='font-serif text-3xl md:text-7xl text-[white] drop-shadow-lg font-light leading-[1.15] mb-4'>
            {displayTitle}
          </h1>
        )}
        {eventDate && (
          <p className='text-sm text-[white] drop-shadow-lg opacity-80 mt-4'>{formatEventDate(eventDate)}</p>
        )}
        <div className='mt-4'>
          <PhotosByContact contact={contact} className='text-[white] drop-shadow-md justify-center' />
        </div>
      </div>
    </div>
  )
}

// Minimal: Image fills viewport with subtle dark overlay, text bottom-left
function MinimalHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <div className='relative h-screen w-full bg-neutral'>
      {/* Background image with subtle dark overlay for text readability */}
      {hasPreview && (
        <>
          {previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='hidden md:block absolute inset-0 w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
          {(previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='md:hidden absolute inset-0 w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
          <div className='absolute inset-0 bg-[black] bg-opacity-25' />
        </>
      )}

      {/* Bottom-LEFT text */}
      <div className='absolute bottom-8 left-8 md:bottom-12 md:left-12 z-10'>
        {customLogo ? (
          <img src={customLogo} alt={logoAltText} className='max-w-[12rem] max-h-[4rem]' />
        ) : (
          <h1 className='font-serif text-2xl md:text-3xl text-[white] drop-shadow-lg font-light'>
            {displayTitle}
          </h1>
        )}
        {eventDate && (
          <p className='text-sm text-[white] drop-shadow-lg opacity-80 mt-2'>{formatEventDate(eventDate)}</p>
        )}
        <div className='mt-2'>
          <PhotosByContact contact={contact} className='text-[white] drop-shadow-md' />
        </div>
      </div>
    </div>
  )
}

// Floating: Neutral page, image centered with equal padding on all sides, title+date in one line above, contact below
function FloatingHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <div className='h-screen w-full flex flex-col bg-neutral p-6 md:p-10 lg:p-14'>
      {/* Top: title + date on one line */}
      <div className='flex items-center justify-center gap-4 md:gap-6 py-3 flex-shrink-0'>
        {customLogo ? (
          <img src={customLogo} alt={logoAltText} className='max-w-[10rem] max-h-[3rem]' />
        ) : (
          <h1 className='font-serif text-xl md:text-2xl text-content font-light whitespace-nowrap'>
            {displayTitle}
          </h1>
        )}
        {eventDate && (
          <>
            <div className='w-[1px] h-4 bg-content opacity-20' />
            <p className='text-sm text-content opacity-60 whitespace-nowrap'>{formatEventDate(eventDate)}</p>
          </>
        )}
      </div>

      {/* Center: image with equal spacing */}
      {hasPreview && (
        <div className='flex-1 min-h-0 overflow-hidden max-w-[1000px] mx-auto w-full'>
          {previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='hidden md:block w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
          {(previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='md:hidden w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
        </div>
      )}

      {/* Bottom: contact */}
      <div className='flex items-center justify-center py-3 flex-shrink-0'>
        <PhotosByContact contact={contact} className='opacity-60' />
      </div>
    </div>
  )
}

// Overlay: Full image (not darkened), left side has dark overlay panel with title/date/contact (like editorial but over the image)
function OverlayHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <div className='relative h-screen w-full'>
      {/* Full background image - NO global dark overlay */}
      {hasPreview && (
        <>
          {previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='hidden md:block absolute inset-0 w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
          {(previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='md:hidden absolute inset-0 w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
        </>
      )}

      {/* Desktop: Left dark panel over the image */}
      <div className='hidden md:flex absolute inset-y-0 left-0 w-[40%] bg-[black] bg-opacity-60 items-center z-10'>
        <div className='px-12 lg:px-16 flex flex-col'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[14rem] max-h-[6rem] mb-4' />
          ) : (
            <h1 className='font-serif text-4xl lg:text-5xl text-[white] font-light leading-tight mb-4'>
              {displayTitle}
            </h1>
          )}
          <div className='w-12 h-[1px] bg-[white] bg-opacity-40 mb-4' />
          {eventDate && (
            <p className='text-sm text-[white] opacity-70 mb-6'>{formatEventDate(eventDate)}</p>
          )}
          <PhotosByContact contact={contact} className='text-[white] opacity-75' />
        </div>
      </div>

      {/* Mobile: Bottom dark panel */}
      <div className='md:hidden absolute inset-x-0 bottom-0 bg-[black] bg-opacity-60 z-10 p-6'>
        <div className='flex flex-col items-center gap-2'>
          {customLogo ? (
            <img src={customLogo} alt={logoAltText} className='max-w-[10rem] max-h-[4rem]' />
          ) : (
            <h1 className='font-serif text-2xl text-[white] font-light text-center'>
              {displayTitle}
            </h1>
          )}
          {eventDate && (
            <p className='text-sm text-[white] opacity-70'>{formatEventDate(eventDate)}</p>
          )}
          <PhotosByContact contact={contact} className='text-[white] opacity-75' />
        </div>
      </div>
    </div>
  )
}

// Grand: Image prominent center, text above and below, all fits in one screen (no scroll)
function GrandHeader({
  customLogo,
  logoAltText,
  displayTitle,
  eventDate,
  previewDesktopUrl,
  previewMobileUrl,
  desktopPosition,
  mobilePosition,
  contact,
}: ThemedHeaderVariantInnerProps) {
  const hasPreview = previewDesktopUrl || previewMobileUrl

  return (
    <div className='h-screen w-full flex flex-col bg-neutral'>
      {/* Top: small text */}
      <div className='flex flex-col items-center pt-6 md:pt-8 pb-4 px-4 flex-shrink-0'>
        {eventDate && (
          <p className='text-sm text-content opacity-60'>{formatEventDate(eventDate)}</p>
        )}
        <PhotosByContact contact={contact} className='opacity-60 mt-1' />
      </div>

      {/* Center: large image */}
      {hasPreview && (
        <div className='flex-1 min-h-0 mx-4 md:mx-12 lg:mx-20 overflow-hidden'>
          {previewDesktopUrl && (
            <img
              src={previewDesktopUrl}
              alt=''
              className='hidden md:block w-full h-full object-cover'
              style={{ objectPosition: desktopPosition }}
            />
          )}
          {(previewMobileUrl || previewDesktopUrl) && (
            <img
              src={previewMobileUrl || previewDesktopUrl!}
              alt=''
              className='md:hidden w-full h-full object-cover'
              style={{ objectPosition: previewMobileUrl ? mobilePosition : desktopPosition }}
            />
          )}
        </div>
      )}

      {/* Bottom: title */}
      <div className='flex flex-col items-center py-5 md:py-8 px-4 flex-shrink-0'>
        {customLogo ? (
          <img src={customLogo} alt={logoAltText} className='max-w-[14rem] max-h-[4rem] mx-auto' />
        ) : (
          <h1 className='font-serif text-2xl md:text-3xl text-content text-center font-light'>
            {displayTitle}
          </h1>
        )}
      </div>
    </div>
  )
}
